import React from "react";
import {
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Rectangle,
} from "recharts";
import { ScatterChart, Scatter } from "recharts";
import { chartColors } from "../../pages/Notifications/constants/notificationConstants";

const HeatMapChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
    );
  }

  // Transform data for heatmap visualization
  const transformedData = [];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  days.forEach((day, dayIndex) => {
    hours.forEach((hour) => {
      const dataPoint = data.find(
        (d) => d.dayOfWeek === dayIndex && d.hour === hour
      );
      transformedData.push({
        day: dayIndex,
        hour,
        value: dataPoint ? dataPoint.value : 0,
        dayLabel: day,
      });
    });
  });

  const getColor = (value) => {
    const max = Math.max(...transformedData.map((d) => d.value));
    const intensity = value / max;
    
    if (intensity === 0) return "#f3f4f6";
    if (intensity < 0.25) return "#ddd6fe";
    if (intensity < 0.5) return "#a78bfa";
    if (intensity < 0.75) return "#7c3aed";
    return "#5b21b6";
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold">{data.dayLabel}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {data.hour}:00 - {data.hour + 1}:00
          </p>
          <p className="text-sm font-medium text-primary">
            {data.value} notifications
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomCell = (props) => {
    const { x, y, width, height, value } = props;
    return (
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={getColor(value)}
        stroke="#e5e7eb"
        strokeWidth={1}
        radius={4}
      />
    );
  };

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Best Time to Send (Last 30 Days)
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart
          margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
          data={transformedData}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            dataKey="hour"
            domain={[0, 23]}
            ticks={hours.filter((h) => h % 3 === 0)}
            tickFormatter={(hour) => `${hour}:00`}
            stroke="#6b7280"
          />
          <YAxis
            type="number"
            dataKey="day"
            domain={[0, 6]}
            ticks={[0, 1, 2, 3, 4, 5, 6]}
            tickFormatter={(day) => days[day]}
            stroke="#6b7280"
          />
          <Tooltip content={<CustomTooltip />} />
          <Scatter
            data={transformedData}
            fill="#8884d8"
            shape={<CustomCell />}
          />
        </ScatterChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center mt-4 space-x-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">Less</span>
        <div className="flex space-x-1">
          {["#f3f4f6", "#ddd6fe", "#a78bfa", "#7c3aed", "#5b21b6"].map(
            (color) => (
              <div
                key={color}
                className="w-4 h-4 rounded"
                style={{ backgroundColor: color }}
              />
            )
          )}
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">More</span>
      </div>
    </div>
  );
};

export default HeatMapChart;
